import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TrendingSidebar() {
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrending();
  }, []);

  const fetchTrending = async () => {
    try {
      const response = await axios.get(`${API}/articles/trending/list?limit=8`);
      setTrendingArticles(response.data);
    } catch (err) {
      console.error('Error fetching trending articles:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <aside className="w-64 bg-zinc-950 border-r border-zinc-800 p-6">
        <div className="mb-8">
          <h2 className="text-sm font-bold tracking-widest mb-4 text-gray-400">TRENDING NOW</h2>
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-4 bg-zinc-800 animate-pulse rounded"></div>
            ))}
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="w-64 bg-black border-r border-zinc-900 p-6 h-screen overflow-y-auto sticky top-0 hidden lg:block">
      {/* Trending Section - Forbes Style */}
      <div className="mb-10">
        <h2 className="text-xs font-light tracking-widest mb-8 text-gray-500 uppercase">
          Trending Now
        </h2>
        <div className="space-y-6">
          {trendingArticles.map((article, index) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="block group"
            >
              <div className="flex items-start gap-4">
                <span className="text-3xl font-extralight text-zinc-800 group-hover:text-[#00FF7F] transition-colors flex-shrink-0 leading-none">
                  {(index + 1).toString().padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0 pt-1">
                  <h3 className="text-sm font-light leading-snug text-gray-300 group-hover:text-white transition-colors line-clamp-3">
                    {article.title}
                  </h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Categories Section - Forbes Style */}
      <div className="mt-10 pt-8 border-t border-zinc-900">
        <h2 className="text-xs font-light tracking-widest mb-6 text-gray-500 uppercase">
          Categories
        </h2>
        <div className="space-y-4">
          {['Trading', 'AI & Technology', 'Business', 'Lifestyle'].map(category => (
            <Link
              key={category}
              to={`/category/${category.toLowerCase().replace(/\s+/g, '-').replace('&', 'and')}`}
              className="block text-sm text-gray-400 hover:text-white transition-colors font-extralight"
            >
              {category}
            </Link>
          ))}
        </div>
      </div>

      {/* Social Links - Forbes Style */}
      <div className="mt-10 pt-8 border-t border-zinc-900">
        <h2 className="text-xs font-light tracking-widest mb-6 text-gray-500 uppercase">Follow</h2>
        <div className="flex gap-5">
          <a
            href="https://youtube.com/@johnghattiworldwide"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </a>
          <a
            href="https://www.instagram.com/john_ghatti__ceo_?igsh=MWM2NmYxdXV0a25naw%3D%3D&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a
            href="https://www.tiktok.com/@johnghatti?_t=ZS-90nnpBKxySK&_r=1"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </a>
        </div>
      </div>
    </aside>
  );
}

export default TrendingSidebar;
